import React, { useEffect, useState } from "react";
import { useNavigate, useParams } from "react-router-dom";
import {
  Alert,
  Card,
  CardBody,
  CardHeader,
  Col,
  Container,
  Form,
  FormFeedback,
  FormGroup,
  Input,
  Label,
  Row,
} from "reactstrap";

import { adminPaths } from "admin/paths";
import { normalizeSlug } from "admin/contentUtils";
import AdminFormActions from "components/admin/AdminFormActions";
import AdminPageHeader from "components/admin/AdminPageHeader";
import { sitePagesRepo } from "data/repositories/sitePagesRepo";

function emptyPage() {
  return {
    id: "",
    slug: "",
    path: "",
    title: "",
    subtitle: "",
    seoDescription: "",
    heroImage: "",
    bodyHtml: "",
    sectionsJson: "[]",
    sortOrder: 0,
    isPublished: true,
  };
}

function toForm(page) {
  return {
    ...emptyPage(),
    ...page,
    sectionsJson: JSON.stringify(page.sections || [], null, 2),
    isPublished: page.isPublished !== false,
  };
}

export default function SitePageEdit() {
  const navigate = useNavigate();
  const params = useParams();
  const pageId = params.id;
  const isNew = pageId === "new" || !pageId;

  const [form, setForm] = useState(emptyPage());
  const [error, setError] = useState("");
  const [errors, setErrors] = useState({});
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    setError("");
    if (isNew) {
      setForm(emptyPage());
      setErrors({});
      return;
    }
    const current = sitePagesRepo.getById(pageId);
    if (!current) {
      setError("Sayfa bulunamadı");
      return;
    }
    setForm(toForm(current));
    setErrors({});
  }, [isNew, pageId]);

  const setField = (key, value) => setForm((prev) => ({ ...prev, [key]: value }));

  const onSave = (e) => {
    e.preventDefault();
    setError("");
    const nextErrors = {};
    const slug = normalizeSlug(form.slug || form.id || form.title);
    if (!slug) nextErrors.slug = "Slug zorunludur.";
    else if (isNew && sitePagesRepo.getById(slug)) nextErrors.slug = "Bu slug zaten var.";
    if (!form.title.trim()) nextErrors.title = "Başlık zorunludur.";

    let sections = [];
    try {
      sections = JSON.parse(form.sectionsJson || "[]");
      if (!Array.isArray(sections)) throw new Error("sections dizi olmalı");
    } catch (parseError) {
      nextErrors.sectionsJson = "Bölümler geçerli JSON dizi olmalı.";
    }

    setErrors(nextErrors);
    if (Object.keys(nextErrors).length) return;

    const path = (form.path.trim() || `/${slug}`).replace(/\/{2,}/g, "/");
    const payload = {
      id: slug,
      slug,
      path: path.startsWith("/") ? path : `/${path}`,
      title: form.title.trim(),
      subtitle: form.subtitle.trim(),
      seoDescription: form.seoDescription.trim(),
      heroImage: form.heroImage.trim(),
      bodyHtml: form.bodyHtml,
      sections,
      sortOrder: Number(form.sortOrder) || 0,
      isPublished: Boolean(form.isPublished),
    };

    try {
      setSaving(true);
      if (isNew) sitePagesRepo.create(payload);
      else sitePagesRepo.update(pageId, payload);
      navigate(`/admin${adminPaths.content.sitePages.list}`, {
        state: { status: { color: "success", message: "Site sayfası kaydedildi." } },
      });
    } catch (saveError) {
      setError(saveError?.message || "Kaydedilemedi");
    } finally {
      setSaving(false);
    }
  };

  return (
    <>
      <AdminPageHeader
        gradient="info"
        title={isNew ? "Yeni site sayfası" : "Site sayfası düzenle"}
        description="Portal marketing rotasında yayınlanır."
        backAction={() => navigate(`/admin${adminPaths.content.sitePages.list}`)}
      />

      <Container className="mt--6" fluid>
        <Row>
          <Col xl="10">
            <Card className="shadow">
              <CardHeader className="border-0">
                <h3 className="mb-0">Detay</h3>
              </CardHeader>
              <CardBody>
                {error ? <Alert color="danger">{error}</Alert> : null}
                <Form onSubmit={onSave}>
                  <Row>
                    <Col md="6">
                      <FormGroup>
                        <Label>Başlık</Label>
                        <Input
                          value={form.title}
                          invalid={Boolean(errors.title)}
                          onChange={(e) => setField("title", e.target.value)}
                        />
                        <FormFeedback>{errors.title}</FormFeedback>
                      </FormGroup>
                    </Col>
                    <Col md="3">
                      <FormGroup>
                        <Label>Slug</Label>
                        <Input
                          value={form.slug}
                          disabled={!isNew}
                          invalid={Boolean(errors.slug)}
                          onChange={(e) => setField("slug", normalizeSlug(e.target.value))}
                          placeholder="about"
                        />
                        <FormFeedback>{errors.slug}</FormFeedback>
                      </FormGroup>
                    </Col>
                    <Col md="3">
                      <FormGroup>
                        <Label>Yol</Label>
                        <Input
                          value={form.path}
                          onChange={(e) => setField("path", e.target.value)}
                          placeholder="/about"
                        />
                      </FormGroup>
                    </Col>
                    <Col md="12">
                      <FormGroup>
                        <Label>Alt başlık</Label>
                        <Input value={form.subtitle} onChange={(e) => setField("subtitle", e.target.value)} />
                      </FormGroup>
                    </Col>
                    <Col md="12">
                      <FormGroup>
                        <Label>SEO açıklama</Label>
                        <Input
                          value={form.seoDescription}
                          onChange={(e) => setField("seoDescription", e.target.value)}
                        />
                      </FormGroup>
                    </Col>
                    <Col md="8">
                      <FormGroup>
                        <Label>Hero görsel URL</Label>
                        <Input value={form.heroImage} onChange={(e) => setField("heroImage", e.target.value)} />
                      </FormGroup>
                    </Col>
                    <Col md="4">
                      <FormGroup>
                        <Label>Sıra</Label>
                        <Input
                          type="number"
                          value={form.sortOrder}
                          onChange={(e) => setField("sortOrder", e.target.value)}
                        />
                      </FormGroup>
                    </Col>
                    <Col md="12">
                      <FormGroup check className="mb-3">
                        <Input
                          id="site-published"
                          type="checkbox"
                          checked={Boolean(form.isPublished)}
                          onChange={(e) => setField("isPublished", e.target.checked)}
                        />
                        <Label check htmlFor="site-published">
                          Yayında
                        </Label>
                      </FormGroup>
                    </Col>
                    <Col md="12">
                      <FormGroup>
                        <Label>Serbest HTML (isteğe bağlı)</Label>
                        <Input
                          type="textarea"
                          rows={6}
                          value={form.bodyHtml}
                          onChange={(e) => setField("bodyHtml", e.target.value)}
                          style={{ fontFamily: "ui-monospace, SFMono-Regular, Menlo, monospace", fontSize: 13 }}
                        />
                      </FormGroup>
                    </Col>
                    <Col md="12">
                      <FormGroup>
                        <Label>
                          Bölümler (JSON) — {"{"} title, paragraphs[], items[] {"}"}
                        </Label>
                        <Input
                          type="textarea"
                          rows={12}
                          value={form.sectionsJson}
                          invalid={Boolean(errors.sectionsJson)}
                          onChange={(e) => setField("sectionsJson", e.target.value)}
                          style={{ fontFamily: "ui-monospace, SFMono-Regular, Menlo, monospace", fontSize: 13 }}
                        />
                        <FormFeedback>{errors.sectionsJson}</FormFeedback>
                      </FormGroup>
                    </Col>
                  </Row>
                  <AdminFormActions
                    saving={saving}
                    onCancel={() => navigate(`/admin${adminPaths.content.sitePages.list}`)}
                  />
                </Form>
              </CardBody>
            </Card>
          </Col>
        </Row>
      </Container>
    </>
  );
}
